import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';

const SEVERITY_COLORS = {
  CRITICAL: '#dc2626',
  HIGH: '#ea580c',
  MEDIUM: '#d97706',
  LOW: '#2563eb',
  INFO: '#6b7280',
};

const LAYER_COLORS = {
  COMPUTE:  '#3b82f6',
  IDENTITY: '#8b5cf6',
  NETWORK:  '#10b981',
  DATABASE: '#f59e0b',
  CODE:     '#ec4899',
};

export default function OverviewTab({ stats, orgId }) {
  const { t } = useTranslation();
  const [findingSummary, setFindingSummary] = useState(null);
  const [assetCounts, setAssetCounts] = useState(null);
  const [riskSummary, setRiskSummary] = useState(null);

  useEffect(() => {
    const params = orgId ? { orgId } : {};
    axios.get('/iasm/findings/summary', { params }).then(r => setFindingSummary(r.data)).catch(() => {});
    axios.get('/iasm/assets/counts', { params }).then(r => setAssetCounts(r.data)).catch(() => {});
    axios.get('/iasm/risk-summary', { params }).then(r => setRiskSummary(r.data)).catch(() => {});
  }, [orgId]);

  const maxBlast = riskSummary?.topBlastRadiusAssets?.[0]?.blastRadius || 1;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
      <SectionCard title={t("OverviewTab.findingsBySeverity")} icon="fa-exclamation-triangle">
        {findingSummary ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {Object.entries(findingSummary.bySeverity || {}).map(([sev, cnt]) => (
              <div key={sev} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  display: 'inline-block', width: '80px', fontSize: '12px', fontWeight: 600,
                  color: SEVERITY_COLORS[sev] || '#6b7280',
                }}>{sev}</span>
                <div style={{ flex: 1, background: '#f3f4f6', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, (cnt / (findingSummary.openCount || 1)) * 100)}%`,
                    height: '100%',
                    background: SEVERITY_COLORS[sev] || '#6b7280',
                    borderRadius: '4px',
                  }} />
                </div>
                <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '24px', textAlign: 'right' }}>{cnt}</span>
              </div>
            ))}
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
              {t("OverviewTab.totalOpenFindings")} <strong>{findingSummary.openCount}</strong>
            </div>
          </div>
        ) : <Skeleton />}
      </SectionCard>

      <SectionCard title={t("OverviewTab.assetsByLayer")} icon="fa-layer-group">
        {assetCounts ? (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
              {Object.entries(assetCounts.byLayer || {}).map(([layer, cnt]) => (
                <div key={layer} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    display: 'inline-block', width: '80px', fontSize: '12px', fontWeight: 600,
                    color: LAYER_COLORS[layer] || '#6b7280',
                  }}>{layer}</span>
                  <div style={{ flex: 1, background: '#f3f4f6', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(100, (cnt / (assetCounts.totalAssets || 1)) * 100)}%`,
                      height: '100%',
                      background: LAYER_COLORS[layer] || '#6b7280',
                      borderRadius: '4px',
                    }} />
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '24px', textAlign: 'right' }}>{cnt}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              {t("OverviewTab.totalAssets")} <strong>{assetCounts.totalAssets}</strong>
            </div>
          </div>
        ) : <Skeleton />}
      </SectionCard>

      <SectionCard title={t("OverviewTab.topFindingTypes")} icon="fa-list">
        {findingSummary ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Object.entries(findingSummary.byType || {}).slice(0, 6).map(([type, cnt]) => (
              <div key={type} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: '#f9fafb', borderRadius: '6px',
              }}>
                <span style={{ fontSize: '13px', color: '#374151' }}>{type.replace(/_/g, ' ')}</span>
                <span style={{
                  fontSize: '12px', fontWeight: 600, background: '#fee2e2',
                  color: '#991b1b', padding: '2px 8px', borderRadius: '4px',
                }}>{cnt}</span>
              </div>
            ))}
          </div>
        ) : <Skeleton />}
      </SectionCard>

      <SectionCard title={t("OverviewTab.assetTypeBreakdown")} icon="fa-th">
        {assetCounts ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(assetCounts.byType || {}).map(([type, cnt]) => (
              <div key={type} style={{
                padding: '6px 12px', background: '#eff6ff', border: '1px solid #bfdbfe',
                borderRadius: '6px', fontSize: '12px', color: '#1d4ed8',
              }}>
                {type.replace(/_/g, ' ')}: <strong>{cnt}</strong>
              </div>
            ))}
          </div>
        ) : <Skeleton />}
      </SectionCard>

      <SectionCard title={t("OverviewTab.topRiskyAssets")} icon="fa-fire">
        {riskSummary ? (
          riskSummary.topRiskyAssets?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {riskSummary.topRiskyAssets.map(a => (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 12px', background: '#f9fafb', borderRadius: '6px',
                }}>
                  <RiskGauge score={a.riskScore} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>{a.assetType?.replace(/_/g, ' ')} · {a.layer}</div>
                  </div>
                  {a.blastRadius > 0 && (
                    <span style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                      <i className="fas fa-project-diagram" style={{ marginRight: '3px' }} />{a.blastRadius}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: '20px' }}>
              {t("OverviewTab.runSecurityAuditEmpty")}
            </div>
          )
        ) : <Skeleton />}
      </SectionCard>

      <div style={{ gridColumn: '1 / -1' }}>
        <SectionCard title={t("OverviewTab.blastRadiusHighImpactAssets")} icon="fa-radiation">
          <p style={{ margin: '0 0 14px', fontSize: '12px', color: '#6b7280' }}>
            {t("OverviewTab.blastRadiusDescription")}
          </p>
          {riskSummary ? (
            riskSummary.topBlastRadiusAssets?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {riskSummary.topBlastRadiusAssets.map(a => {
                  const blastPct = Math.min(100, (a.blastRadius / maxBlast) * 100);
                  const color = blastPct >= 75 ? '#dc2626' : blastPct >= 40 ? '#ea580c' : '#d97706';
                  return (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '130px', flexShrink: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                        <div style={{ fontSize: '10px', color: '#9ca3af' }}>{a.assetType?.replace(/_/g, ' ')} · {a.layer}</div>
                      </div>
                      <div style={{ flex: 1, background: '#f3f4f6', borderRadius: '4px', height: '10px', overflow: 'hidden' }}>
                        <div style={{ width: `${blastPct}%`, height: '100%', background: color, borderRadius: '4px', transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ width: '90px', textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                        <i className="fas fa-project-diagram" style={{ color, fontSize: '11px' }} />
                        <span style={{ fontSize: '13px', fontWeight: 700, color }}>{t("OverviewTab.blastRadiusAssetsCount", { n: a.blastRadius })}</span>
                      </div>
                      <div style={{ width: '36px', flexShrink: 0 }}>
                        <RiskGauge score={a.riskScore} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: '20px' }}>
                {t("OverviewTab.runSyncBlastRadiusEmpty")}
              </div>
            )
          ) : <Skeleton />}
        </SectionCard>
      </div>

      <SectionCard title={t("OverviewTab.remediationSla")} icon="fa-clock">
        {riskSummary ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <SlaMetric
              label={t("OverviewTab.slaOverdue")}
              value={riskSummary.sla?.overdue ?? 0}
              color={riskSummary.sla?.overdue > 0 ? '#dc2626' : '#10b981'}
              icon="fa-exclamation-circle"
            />
            <SlaMetric
              label={t("OverviewTab.slaDueThisWeek")}
              value={riskSummary.sla?.dueThisWeek ?? 0}
              color={riskSummary.sla?.dueThisWeek > 0 ? '#d97706' : '#10b981'}
              icon="fa-calendar-alt"
            />
            <SlaMetric
              label={t("OverviewTab.slaUnassigned")}
              value={riskSummary.sla?.unassignedOpen ?? 0}
              color={riskSummary.sla?.unassignedOpen > 0 ? '#7c3aed' : '#10b981'}
              icon="fa-user-slash"
            />
            <SlaMetric
              label={t("OverviewTab.slaAvgDaysOpen")}
              value={riskSummary.sla?.avgDaysOpen ?? 0}
              color={riskSummary.sla?.avgDaysOpen > 30 ? '#ea580c' : '#374151'}
              icon="fa-hourglass-half"
            />
          </div>
        ) : <Skeleton />}
      </SectionCard>
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb',
      borderRadius: '8px', padding: '20px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <i className={`fas ${icon}`} style={{ color: '#6b7280', fontSize: '14px' }} />
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#111827' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ height: '20px', background: '#f3f4f6', borderRadius: '4px', animation: 'pulse 1.5s infinite' }} />
      ))}
    </div>
  );
}

function RiskGauge({ score }) {
  const color = score >= 70 ? '#dc2626' : score >= 40 ? '#ea580c' : score >= 15 ? '#d97706' : '#10b981';
  return (
    <div style={{
      width: '36px', height: '36px', borderRadius: '50%',
      border: `3px solid ${color}`, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: '11px', fontWeight: 700, color, flexShrink: 0,
    }}>{score}</div>
  );
}

function SlaMetric({ label, value, color, icon }) {
  return (
    <div style={{
      padding: '12px', background: '#f9fafb', borderRadius: '8px',
      display: 'flex', flexDirection: 'column', gap: '4px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <i className={`fas ${icon}`} style={{ color, fontSize: '12px' }} />
        <span style={{ fontSize: '11px', color: '#6b7280' }}>{label}</span>
      </div>
      <div style={{ fontSize: '22px', fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
